import { Column } from "primereact/column";
import { Dropdown } from 'primereact/dropdown';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from "react-i18next";
import { ReactComponent as ArrowDownIcon } from '../../assets/images/arrow_down.svg';
import { ReactComponent as ArrowUpIcon } from '../../assets/images/arrow_up.svg';
import { ReactComponent as CriticalIcon } from '../../assets/images/critical-triangle-Icon.svg';
import DataTable, {
    FILTER_TYPES,
    DataTableContext,
    DataTableProvider,
} from "../../component/datatable2";
import { MessageContext } from '../../context/MessageContext';
import { useDashboardData } from '../../context/DashboardContext';
import { RenderedHelper } from '../../utils/render/RenderedHelper';
import './Widget.scss';
import { DeviceCurrentStateService } from '../../service/DeviceCurrentStateService';

const CriticalProductConditionWidget = () => {
    return (
        <DataTableProvider>
            <CriticalProductConditionWidgetImpl />
        </DataTableProvider>
    );
};

const CriticalProductConditionToolbar = ({
    rowsPerPageOptions,
    defaultSearchCriteria,
    isCollapsed,
    toggleCollapse,
}) => {
    const { searchCriteria, setSearchCriteria } = useContext(DataTableContext);

    return (
        <div className="flex align-items-center gap-2">
            {!isCollapsed && (
                <Dropdown
                    className="small"
                    id="rowsPerPage"
                    options={rowsPerPageOptions}
                    value={searchCriteria?.rows ?? defaultSearchCriteria.rows}
                    onChange={e =>
                        setSearchCriteria(prev => ({
                            ...prev,
                            first: 0,
                            rows: e.value,
                            timestamp: Date.now(),
                        }))
                    }
                />
            )}

            {isCollapsed ? (
                <ArrowUpIcon className="collapse-button" onClick={toggleCollapse} />
            ) : (
                <ArrowDownIcon className="collapse-button" onClick={toggleCollapse} />
            )}
        </div>
    );
};

const CriticalProductConditionWidgetImpl = () => {

    const { t } = useTranslation();
    const isMounted = useRef(false);
    const rowsPerPageOptions = [5, 10, 20, 50];
    const { showErrorMessage } = useContext(MessageContext);
    const { setSearchCriteria } = useContext(DataTableContext);
    const { locationOptions, cityOptions, deviceOptions, productOptions } = useDashboardData();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const defaultSearchCriteria = useMemo(() => ({
        first: 0,
        rows: 5,
        sortField: 'quantity',
        sortOrder: 1,
        baseQuery: '',
        filters: {}
    }), []);

    useEffect(() => {
        isMounted.current = true;
        setSearchCriteria(defaultSearchCriteria);
        return () => {
            isMounted.current = false;
        }
    }, []);
    const apiValueConverter = value => value.map(r => r.id).join(',');

    const filterConfig = useMemo(() => [
        {
            fieldName: 'productName',
            label: t('criticalProductConditionWidget.product'),
            type: FILTER_TYPES.LIST,
            params: productOptions,
            apiParamName: 'products',
            apiValueConverter
        },
        {
            fieldName: 'location.name',
            label: t('criticalProductConditionWidget.locationName'),
            type: FILTER_TYPES.LIST,
            params: locationOptions,
            apiParamName: 'locations',
            apiValueConverter
        },
        {
            fieldName: 'location.city',
            label: t('criticalProductConditionWidget.city'),
            type: FILTER_TYPES.LIST,
            params: cityOptions,
            apiParamName: 'location_cities',
            apiValueConverter
        },
        {
            fieldName: 'deviceName',
            label: t('criticalProductConditionWidget.device'),
            type: FILTER_TYPES.LIST,
            params: deviceOptions,
            apiParamName: 'devices',
            apiValueConverter
        },
        {
            fieldName: 'positionRow',
            label: t('criticalProductConditionWidget.row'),
            type: FILTER_TYPES.NUMBER_RANGE,
            apiParamName: 'position_row',
            params: { maxValue: 30 }
        },
        {
            fieldName: 'positionCol',
            label: t('criticalProductConditionWidget.column'),
            type: FILTER_TYPES.NUMBER_RANGE,
            apiParamName: 'position_col',
            params: { maxValue: 30 }
        },
        {
            fieldName: 'quantity',
            label: t('criticalProductConditionWidget.stockQuantity'),
            type: FILTER_TYPES.NUMBER_RANGE,
            apiParamName: 'quantity',
            params: { maxValue: 300 }
        },
        {
            fieldName: 'status',
            label: t('criticalProductConditionWidget.status'),
            type: FILTER_TYPES.LIST,
            params: [
                { id: 'CRITICAL', label: t('ProductConditionStatus.CRITICAL') },
                { id: 'WARNING', label: t('ProductConditionStatus.WARNING') }
            ],
            apiParamName: 'statuses',
            apiValueConverter: value => value.map(r => r.id).join(',')
        },
    ], [productOptions, locationOptions, cityOptions, deviceOptions]);

    const normalizeCriteria = (criteria) => ({
        ...defaultSearchCriteria,
        ...criteria,
        first: criteria?.first ?? defaultSearchCriteria.first,
        rows: criteria?.rows ?? defaultSearchCriteria.rows,
        sortField:
            !criteria?.sortField || criteria.sortField === 'null'
                ? defaultSearchCriteria.sortField
                : criteria.sortField,
        sortOrder:
            criteria?.sortOrder === null || criteria?.sortOrder === undefined
                ? defaultSearchCriteria.sortOrder
                : criteria.sortOrder,
        baseQuery: criteria?.baseQuery ?? defaultSearchCriteria.baseQuery,
        filters: criteria?.filters ?? defaultSearchCriteria.filters,
    });

    const fetchData = async (criteria) => {
        try {
            return await DeviceCurrentStateService.getCriticalProductConditionList(normalizeCriteria(criteria));
        } catch (err) {
            showErrorMessage(t('criticalProductConditionWidget.error.fetch'));
            return { data: [], totalRecords: 0 };
        }
    };

    const toggleCollapse = () => {
        setIsCollapsed(!isCollapsed);
    }

    const tableToolbarLeft = (
        <div className='flex align-items-center gap-2'>
            <CriticalIcon />
            <span className='widget-name'>{t('criticalProductConditionWidget.widgetName')}</span>
        </div>
    );

    const tableToolbarRight = (
        <CriticalProductConditionToolbar
            rowsPerPageOptions={rowsPerPageOptions}
            defaultSearchCriteria={defaultSearchCriteria}
            isCollapsed={isCollapsed}
            toggleCollapse={toggleCollapse}
        />
    );

    return (
        <div className={`critical-product-condition-widget widget data-card ${isCollapsed ? 'collapsed' : ''}`}>
            <DataTable
                tableToolbarLeft={tableToolbarLeft}
                tableToolbarRight={tableToolbarRight}
                globalSearchPosition="none"
                fetchData={fetchData}
                filterConfig={filterConfig}
                defaultSearchCriteria={defaultSearchCriteria}
                showRowsPerPage={false}
                showPaginator={false}
                minWidth="1000px"
            >
                <Column field="productName" header={t('criticalProductConditionWidget.product')} sortable sortField="productName" id="productName"
                    style={{ minWidth: '160px', maxWidth: '220px' }}
                />
                <Column field="location.name" header={t('criticalProductConditionWidget.locationName')} sortable sortField="location.name" id="locationName"
                    headerClassName="dt2-header-wrap"
                    style={{ minWidth: '150px', maxWidth: '190px' }}
                />
                <Column field="location.city" header={t('criticalProductConditionWidget.city')} sortable sortField="location.city" id="city"
                    style={{ minWidth: '120px', maxWidth: '150px' }}
                />
                <Column field="device.name" header={t('criticalProductConditionWidget.device')} sortable sortField="deviceName" id="deviceName"
                    style={{ minWidth: '140px', maxWidth: '180px' }}
                />
                <Column field="positionRow" header={t('criticalProductConditionWidget.row')} sortable sortField="positionRow" id="positionRow"
                    headerClassName="dt2-header-wrap"
                    style={{ minWidth: '90px', maxWidth: '110px' }}
                />
                <Column field="positionCol" header={t('criticalProductConditionWidget.column')} sortable sortField="positionCol" id="positionCol"
                    headerClassName="dt2-header-wrap"
                    style={{ minWidth: '90px', maxWidth: '120px' }}
                />
                <Column field="stockQuantity" header={t('criticalProductConditionWidget.stockQuantity')} sortable sortField="quantity" id="stockQuantity"
                    headerClassName="dt2-header-wrap"
                    body={row => `${row.quantity || 0}/${row.maxQuantity || 0}`}
                    style={{ minWidth: '120px', maxWidth: '150px' }}
                />
                <Column field="status" header={t('criticalProductConditionWidget.status')} sortable id="status"
                    body={row => RenderedHelper.formatEnum(row.status, 'ProductConditionStatus')}
                    style={{ minWidth: '120px', maxWidth: '150px' }}
                />
            </DataTable>
        </div>
    )

}

export default CriticalProductConditionWidget;